/**
 * Durable outbound queue.
 *
 * The spec calls for offline-FIRST, not offline-tolerant, and until now
 * a message typed while the line was down was simply lost. On retail
 * internet -- the stated assumption -- that happens weekly, and it is
 * the kind of failure that makes staff stop trusting the tool and go
 * back to the phone.
 *
 * localStorage rather than IndexedDB: the queue is a handful of short
 * strings, it must survive a crash, and it has to be readable
 * synchronously at boot to show a count before anything else loads.
 */
export interface Queued {
  readonly id: string;
  readonly path: string;
  readonly body: string;
  readonly at: number;
}

const KEY = 'kestrelay.outbox';

const read = (): Queued[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as Queued[];
  } catch {
    return [];
  }
};

const write = (items: Queued[]): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Storage full or blocked. Nothing useful to do, and throwing here
    // would lose the message we were trying to save.
  }
};

export const queued = (): number => read().length;

export const enqueue = (path: string, body: string): void => {
  write([
    ...read(),
    { id: crypto.randomUUID(), path, body, at: Date.now() },
  ]);
};

/**
 * Send what is waiting, oldest first.
 *
 * Order matters: two messages typed during an outage must arrive in
 * the order they were written, so this stops at the first failure
 * rather than skipping ahead. A 4xx is dropped rather than retried
 * forever -- the server has refused it, and a poisoned entry would
 * block everything behind it.
 */
/**
 * Statuses worth waiting on. A 401 is almost always an access token
 * that expired during the outage -- the shell refreshes it within
 * fifteen minutes -- and discarding the message instead lost
 * something a person typed, which is the one thing this queue exists
 * to prevent. Only a genuinely poisoned entry (400, 413, 422) is
 * dropped, because it would block everything behind it forever.
 */
const RETRY = new Set([401, 403, 408, 425, 429]);

const drop = (id: string): void => {
  write(read().filter((q) => q.id !== id));
};

/** One flush at a time; the board calls this every five seconds. */
let running = false;

export const flush = async (
  send: (path: string, body: string) => Promise<Response>,
): Promise<number> => {
  // A send slower than the five-second tick let a second pass start
  // on the same snapshot and post the first item twice.
  if (running) return 0;
  running = true;
  let sent = 0;

  try {
    for (;;) {
      const next = read()[0];
      if (!next) break;

      let response: Response;
      try {
        response = await send(next.path, next.body);
      } catch {
        break;
      }

      if (!response.ok
        && (RETRY.has(response.status) || response.status >= 500)) {
        break;
      }

      // Re-read rather than write back the snapshot taken before the
      // await: a message typed while that request was in flight was
      // otherwise erased by this line.
      drop(next.id);
      sent += 1;
    }
  } finally {
    running = false;
  }

  return sent;
};

